<template lang="pug">
  .rg-img-gallery.gallery(:class="[layout]" )
    slot(:images="media")
      Component(
        :is="layout === 'slider' ? 'Carousel' : 'div'"
        :class="[orientation, layout === 'grid-captions' ? 'grid-captions grid-wrap' : '', layout === 'slider' ? 'slider' : 'grid-wrap', 'col-'+columns]"
      )
        .img(v-for="image in media"  :key="image.src" @click="toggleZoom(image.galleryIndex)")
          .img-wrap
            DynamicMedia(
              :widths="[250, 308]"
              :config="image"
              :maxWidth="maxWidth"
              :style="scale ? {transform: 'scale('+(scale/100)+')'} : null"
            )
            h4.caption(v-if="layout === 'grid-captions'") {{image.alt}}
    Media(
      v-if="zoom && zoomType === 'vertical'"
      layout="zoomed"
      :modelValue="activeIndex"
      :gallery="images"
      @close="zoom = false"
    )
    CarouselModal( v-if="zoom && zoomType === 'horizontal'" :value="activeIndex" @close="zoom = false")
      DynamicMedia.zoom-horiz-img(
        v-for="image in images"
        :key="image.galleryIndex"
        :config="image"
        :contain="true"
        :maxWidth="image.maxWidth || image.width"
      )
    .pagination(v-if="layout !== 'slider'")
      RogueButton.load-more(
        v-if="pagination === 'loadMore' && media.length < images.length"
        @click="page += 1"
      ) {{ __('Load More') }}
      RogueButton(
        v-if="pagination === 'paged' && pageSize < images.length"
        :disabled="page === 0"
        @click="page -= 1"
      ) {{ __('Previous Page') }}
      RogueButton(
        v-if="pagination === 'paged' && pageSize < images.length"
        :disabled="(page + 1) * pageSize >= images.length"
        @click="page += 1"
      ) {{ __('Next Page') }}
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue'

const Media = defineAsyncComponent(() => import(/* webpackChunkName: "sisu-media" */'./media/Media.vue'))

interface ImageConfig {
  src?: string
  alt?: string
  width?: number
  height?: number
  maxWidth?: number
  galleryIndex?: number
  [key: string]: any
}

type Layout = 'slider' | 'grid' | 'grid-captions'
type Orientation = 'square' | 'landscape' | 'portrait'
type ZoomType = 'horizontal' | 'vertical' | 'none'
type Pagination = 'paged' | 'loadMore' | null

const props = withDefaults(defineProps<{
  images?: ImageConfig[]
  layout?: Layout
  orientation?: Orientation
  // horizontal, vertical, or none
  zoomType?: ZoomType
  pagination?: Pagination
  pageSize?: number
  scale?: number | null
  columns?: number
  maxWidth?: number | null
  zoomFactor?: number | null
}>(), {
  images      : () => [],
  layout      : 'slider',
  orientation : 'square',
  zoomType    : 'vertical',
  pagination  : null,
  pageSize    : 36,
  scale       : null,
  columns     : 4,
  maxWidth    : null,
  zoomFactor  : null,
})

const emit = defineEmits<{
  clickImg: [index: number]
}>()

const zoom = ref(false)
const activeIndex = ref(0)
const page = ref(0)

const media = computed(() => {
  let width = 548
  let height = 548
  if (props.layout === 'grid' || props.layout === 'grid-captions') {
    // High res for grid (which can expand)
    height = 1024
    width = 1024
  } else if (props.orientation === 'landscape') {
    height = 308
  } else if (props.orientation === 'portrait') {
    height = 386
    width = 309
  }

  const configs = props.images.map((img, i) => ({ ...img, width, height, galleryIndex: i }))

  if (props.layout !== 'slider') {
    const pageSize = parseInt(`${props.pageSize}`, 10)
    if (props.pagination === 'paged') {
      const first = page.value * pageSize

      return configs.slice(first, first + pageSize)
    }

    if (props.pagination === 'loadMore') {
      return configs.slice(0, pageSize * (page.value + 1))
    }
  }

  return configs
})

function toggleZoom(index: number) {
  activeIndex.value = index
  if (props.zoomType !== 'none') {
    zoom.value = true
  }
  emit('clickImg', index)
}
</script>
<style lang="scss">
.rg-img-gallery {
  .img {
    cursor: pointer;
    width: 308px;
    overflow: hidden;
    @include mobile() {
      width: 250px;
    }
  }

  .img-wrap {
    overflow: hidden;
  }

  .img-wrap h4.caption {
    font-size: $size-xs;
    margin: 5px 0 0 0;
    text-align: center;
  }

  &.slider {
    .img {
      margin-right: $space-s;
    }
  }

  .pagination {
    margin-top: $space-s;
    display: flex;
    justify-content: space-between;
  }

  .load-more {
    margin: 0 auto;
  }

  .zoom-horiz-img {
    max-width: 100%;
    max-height: 100%;

    .rg-img {
      background-color: transparent;
    }
  }

  &.grid-captions {
    .img {
      cursor: auto;
    }
  }

  &.grid, &.grid-captions {
    overflow: hidden;

    .grid-wrap {
      margin: 0 (-$space-xxs);
      display: flex;
      flex-wrap: wrap;
      justify-content: center;

      .img {
        width: 25%;
        margin-bottom: $space-s;
        padding: 0 $space-xxs;
      }

      &.col-1 .img {
        width: 100%;
      }
      &.col-2 .img {
        width: 50%;
      }
      &.col-3 .img {
        width: 33.3%;
      }
      &.col-4 .img {
        width: 25%;
      } 

      @include not-mobile() {
        &.col-5 .img {
          width: 20%;
        }
        &.col-6 .img {
          width: 16.6%;
        }
        &.col-7 .img {
          width: 14.28%;
        }
        &.col-8 .img {
          width: 12.5%;
        }
        &.col-9 .img {
          width: 11.1%;
        }
        &.col-10 .img {
          width: 10%;
        }
      }
    }
  }
  &.grid-captions {
    .grid-wrap {
      @include mobile() {
        .img {
          width: 33.3%;
        }
      }
      @include tablet() {
        .img {
          width: 15%;
        }
      }
    }
  }
}

.center-media{
  .media-wrap{
    margin: auto;
  }
}

</style>
